import { CSSProperties } from "react";

import { clampPopoverLeft, POPOVER_VIEWPORT_MARGIN } from "@/utils/clampPopoverLeft";

/** Gap between the timing chip and its popup, in px. */
export const POPUP_GAP = 4;

export type TimingDropdownPosition = "bottom-right" | "top-left";

export interface PopupSize {
	width: number;
	height: number;
}

export interface Viewport {
	width: number;
	height: number;
}

/**
 * Places the timing dropdown's popup (the preset list or the mini calendar) as a viewport-fixed
 * box next to its trigger.
 *
 * Both axes are bounded. Vertically the popup flips above the trigger when there is not enough
 * room below (or when `prefer` asks for it and there is room above). Horizontally it is clamped to
 * the viewport against its *measured* width — without that, a popup opened from the Lead Panel
 * (`fixed right-0 w-[450px]`) is placed at the trigger's left edge and the 260px calendar runs off
 * the right of the screen: PREC-4130, where the customer saw four weekday columns and no way to
 * pick a date.
 *
 * `prefer` only expresses the vertical preference; the horizontal placement follows the trigger
 * and the viewport, never the preference.
 */
export const computeTimingPopupStyle = (
	triggerRect: { top: number; bottom: number; left: number },
	popup: PopupSize,
	viewport: Viewport,
	prefer: TimingDropdownPosition
): CSSProperties => {
	const spaceBelow = viewport.height - triggerRect.bottom;
	const spaceAbove = triggerRect.top;
	const needed = popup.height + POPUP_GAP + POPOVER_VIEWPORT_MARGIN;

	const preferTop = prefer === "top-left";
	const openUp = preferTop
		? spaceAbove >= needed || spaceAbove >= spaceBelow
		: spaceBelow < needed && spaceAbove > spaceBelow;

	const style: CSSProperties = {
		position: "fixed",
		left: clampPopoverLeft(triggerRect.left, popup.width, viewport.width),
		// Without this the popup is shrink-to-fit, so the space left of the viewport edge caps the
		// width it reports — the popup measures 150px exactly when it is a 260px calendar hanging
		// off the screen, and the clamp above would be computed from that lie and leave it there.
		width: "max-content",
		zIndex: 9999,
	};

	if (openUp) {
		style.bottom = viewport.height - triggerRect.top + POPUP_GAP;
	} else {
		style.top = triggerRect.bottom + POPUP_GAP;
	}

	return style;
};
